import { Stomp } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import { toast } from 'react-toastify';
import { authService } from '../services/AuthService';
import { getShopBranchIdFromToken, getShopIdFromToken } from '../auth/authUtils';

let stompClient = null;
let configUpdateHandler = null;
let currentTenant = null;
let configSubscriptions = [];
let adminSubscriptions = [];
let reconnectTimeoutId = null;
let isConnecting = false;
let adminTopicsSubscribed = false;
let reconnectAllowed = true;
const cartaConfigListeners = new Set();

const RECONNECT_DELAY_MS = 3000;

const ADMIN_TOAST_EXCLUDED_PATHS = new Set([
    '/',
    '/carta',
    '/estadodelpedido',
    '/notfound',
]);

const buildTenantTopics = (shopId, shopBranchId) => ({
    whatsapp: `/topic/${shopId}/${shopBranchId}/configWhatsapp`,
    deliveryCommon: `/topic/${shopId}/${shopBranchId}/configDeliveryCommon`,
    deliveryCustom: `/topic/${shopId}/${shopBranchId}/configDeliveryCustom`,
});

const resolveTenant = (tenantOverride) => {
    if (tenantOverride?.shopId && tenantOverride?.shopBranchId) {
        return tenantOverride;
    }

    const shopId = getShopIdFromToken();
    const shopBranchId = getShopBranchIdFromToken();
    if (shopId && shopBranchId) {
        return { shopId, shopBranchId };
    }

    return null;
};

const buildConnectHeaders = (tenant) => {
    const headers = {};

    if (tenant?.shopId && tenant?.shopBranchId) {
        headers.shopId = String(tenant.shopId);
        headers.shopBranchId = String(tenant.shopBranchId);
    }

    return headers;
};

const buildSockJsUrl = () => {
    const rawUrl = process.env.REACT_APP_API_WHATSAPP_CONFIGS_SOCKET_ROUTE;
    if (!rawUrl) {
        console.error('Falta REACT_APP_API_WHATSAPP_CONFIGS_SOCKET_ROUTE para el WebSocket.');
        return null;
    }

    try {
        const resolvedUrl = new URL(rawUrl, window.location.origin);
        if (window.location.protocol === 'https:' && resolvedUrl.protocol === 'http:') {
            resolvedUrl.protocol = 'https:';
        }
        return resolvedUrl.toString();
    } catch (error) {
        console.error('URL de WebSocket invalida:', rawUrl, error);
        return rawUrl;
    }
};

const parseMessageBody = (message) => {
    let alertMessage = message.body;
    if (message.isBinaryBody) {
        const decoder = new TextDecoder('utf-8');
        alertMessage = decoder.decode(message._binaryBody);
    }
    return alertMessage;
};

const shouldDeliverAdminToast = () => {
    const session = authService.getSessionData();
    if (!session?.authenticated) {
        return false;
    }

    const path = window.location.pathname.toLowerCase();
    return !ADMIN_TOAST_EXCLUDED_PATHS.has(path);
};

const hasActiveListeners = () => Boolean(configUpdateHandler) || cartaConfigListeners.size > 0 || currentTenant;

const scheduleReconnect = () => {
    if (!reconnectAllowed || reconnectTimeoutId || !hasActiveListeners()) {
        return;
    }

    reconnectTimeoutId = window.setTimeout(() => {
        reconnectTimeoutId = null;
        ensureStompConnected();
    }, RECONNECT_DELAY_MS);
};

const notifyCartaListeners = (config) => {
    cartaConfigListeners.forEach((listener) => {
        try {
            listener(config);
        } catch (error) {
            console.error('Error en listener de configuración de carta:', error);
        }
    });
};

const unsubscribeConfigTopics = () => {
    configSubscriptions.forEach((subscription) => {
        try {
            subscription.unsubscribe();
        } catch (error) {
            console.warn('No se pudo desuscribir topic de configuración', error);
        }
    });
    configSubscriptions = [];
};

const resetAdminSubscriptions = () => {
    adminSubscriptions.forEach((subscription) => {
        try {
            subscription.unsubscribe();
        } catch (error) {
            console.warn('No se pudo desuscribir cola admin de WebSocket', error);
        }
    });
    adminSubscriptions = [];
    adminTopicsSubscribed = false;
};

const subscribeConfigTopics = (tenant, onConfigUpdate) => {
    if (!stompClient?.connected || !tenant) {
        return;
    }

    unsubscribeConfigTopics();
    const topics = buildTenantTopics(tenant.shopId, tenant.shopBranchId);
    const handler = onConfigUpdate || configUpdateHandler;

    configSubscriptions.push(
        stompClient.subscribe(topics.deliveryCommon, (message) => {
            const deliveryConfig = JSON.parse(message.body);
            if (handler) {
                handler((prevConfig) => ({
                    ...prevConfig,
                    deliveryCommon: deliveryConfig,
                }));
            }
        })
    );

    configSubscriptions.push(
        stompClient.subscribe(topics.deliveryCustom, (message) => {
            const deliveryConfig = JSON.parse(message.body);
            if (handler) {
                handler((prevConfig) => ({
                    ...prevConfig,
                    deliveryCustom: deliveryConfig,
                }));
            }
        })
    );

    configSubscriptions.push(
        stompClient.subscribe(topics.whatsapp, (message) => {
            const config = JSON.parse(message.body);
            if (handler) {
                handler(config);
            }
            notifyCartaListeners(config);
        })
    );
};

const subscribeToAdminTopics = async () => {
    if (!stompClient?.connected || adminTopicsSubscribed) {
        return;
    }

    const isAuth = await authService.isAuthenticated();
    if (!isAuth) {
        return;
    }

    adminTopicsSubscribed = true;

    adminSubscriptions.push(
        stompClient.subscribe('/user/queue/notificaciones', (message) => {
            if (!shouldDeliverAdminToast()) {
                return;
            }

            const notificationMessage = parseMessageBody(message);
            toast.info(<div dangerouslySetInnerHTML={{ __html: notificationMessage }} />, {
                position: 'top-right',
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
        })
    );

    adminSubscriptions.push(
        stompClient.subscribe('/user/queue/lowStockAlerts', (message) => {
            if (!shouldDeliverAdminToast()) {
                return;
            }

            const alertMessage = parseMessageBody(message);
            toast.warning(alertMessage, {
                position: 'top-right',
                autoClose: false,
                hideProgressBar: true,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                style: {
                    backgroundColor: 'rgb(78, 176, 223)',
                    color: 'white',
                    fontWeight: 'bold',
                },
            });
        })
    );

    adminSubscriptions.push(
        stompClient.subscribe('/user/queue/expirationProductAlerts', (message) => {
            if (!shouldDeliverAdminToast()) {
                return;
            }

            const alertMessage = parseMessageBody(message);
            toast.warning(<div dangerouslySetInnerHTML={{ __html: alertMessage }} />, {
                position: 'top-right',
                autoClose: false,
                hideProgressBar: true,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                style: {
                    backgroundColor: 'rgb(223,78,78)',
                    color: 'white',
                    fontWeight: 'bold',
                },
            });
        })
    );
};

const needsConfigTopics = () => Boolean(configUpdateHandler) || cartaConfigListeners.size > 0;

const ensureStompConnected = (tenantOverride = null) => {
    if (!hasActiveListeners()) {
        return;
    }

    const tenant = resolveTenant(tenantOverride || currentTenant);

    if (needsConfigTopics() && !tenant) {
        return;
    }

    if (stompClient?.connected) {
        if (tenant) {
            subscribeConfigTopics(tenant);
        }
        if (configUpdateHandler) {
            subscribeToAdminTopics();
        }
        return;
    }

    if (isConnecting) {
        return;
    }

    const socketUrl = buildSockJsUrl();
    if (!socketUrl) {
        return;
    }

    isConnecting = true;
    reconnectAllowed = true;

    if (!stompClient) {
        stompClient = Stomp.over(() => new SockJS(socketUrl));
        stompClient.reconnect_delay = RECONNECT_DELAY_MS;
    }

    const headers = buildConnectHeaders(tenant);

    stompClient.connect(headers, () => {
        isConnecting = false;
        console.log('Conectado al WebSocket');

        const activeTenant = resolveTenant(currentTenant);
        if (activeTenant) {
            subscribeConfigTopics(activeTenant);
        }
        if (configUpdateHandler) {
            subscribeToAdminTopics();
        }
    }, (error) => {
        isConnecting = false;
        unsubscribeConfigTopics();
        resetAdminSubscriptions();
        console.error('Error al conectar WebSocket:', error);
        scheduleReconnect();
    });

    stompClient.onDisconnect = () => {
        isConnecting = false;
        unsubscribeConfigTopics();
        resetAdminSubscriptions();
        scheduleReconnect();
    };
};

/**
 * Cierra el WebSocket y evita reconexiones automáticas (logout / sesión expirada).
 */
export const disconnectWebSocket = ({ allowReconnect = false } = {}) => {
    reconnectAllowed = allowReconnect;

    if (reconnectTimeoutId) {
        window.clearTimeout(reconnectTimeoutId);
        reconnectTimeoutId = null;
    }

    resetAdminSubscriptions();
    unsubscribeConfigTopics();

    if (!allowReconnect) {
        toast.dismiss();
    }

    if (stompClient?.connected) {
        try {
            stompClient.disconnect();
        } catch (error) {
            console.warn('Error al desconectar WebSocket:', error);
        }
    }

    isConnecting = false;
};

/**
 * Tras un login o cambio de sucursal: nueva conexión STOMP con cookie/JWT actual.
 */
export const reconnectAuthenticatedWebSocket = () => {
    if (!configUpdateHandler && !currentTenant && cartaConfigListeners.size === 0) {
        return;
    }

    reconnectAllowed = true;

    const connectAfterTeardown = () => {
        adminTopicsSubscribed = false;
        ensureStompConnected();
    };

    if (stompClient?.connected) {
        resetAdminSubscriptions();
        unsubscribeConfigTopics();
        try {
            stompClient.disconnect(connectAfterTeardown);
        } catch (error) {
            console.warn('Error al reconectar WebSocket:', error);
            connectAfterTeardown();
        }
        return;
    }

    connectAfterTeardown();
};

export const connectWebSocket = (onConfigUpdate, tenantOverride = null) => {
    configUpdateHandler = onConfigUpdate;
    currentTenant = resolveTenant(tenantOverride);
    ensureStompConnected(tenantOverride);
};

export const connectWebSocketForBranch = (shopId, shopBranchId, onConfigUpdate) => {
    currentTenant = { shopId, shopBranchId };
    configUpdateHandler = onConfigUpdate;
    ensureStompConnected(currentTenant);
};

export const updateWebSocketTenant = (shopId, shopBranchId) => {
    if (!shopId || !shopBranchId) {
        return;
    }

    currentTenant = { shopId, shopBranchId };

    if (stompClient?.connected) {
        subscribeConfigTopics(currentTenant);
        if (configUpdateHandler) {
            resetAdminSubscriptions();
            subscribeToAdminTopics();
        }
        return;
    }

    ensureStompConnected(currentTenant);
};

export const connectCartaConfigSync = (shopBranchId, onConfigUpdate) => {
    if (!shopBranchId) {
        return () => {};
    }

    const listener = (config) => {
        if (Number(config?.shopBranchId) === Number(shopBranchId)) {
            onConfigUpdate(config);
        }
    };

    cartaConfigListeners.add(listener);

    return () => {
        cartaConfigListeners.delete(listener);
    };
};

const sendMessage = (destination, params) => {
    if (stompClient && stompClient.connected) {
        stompClient.send(destination, {}, JSON.stringify(params));
        return true;
    }

    console.warn('WebSocket no está conectado');
    return false;
};

export const sendConfigUpdateWhatsapp = (params) => sendMessage('/app/updateConfigWhatsapp', params);

export const sendConfigUpdateDelivery = (params) => sendMessage('/app/updateConfigDeliveryCommon', params);

export const sendConfigUpdateDeliveryCustom = (params) => sendMessage('/app/updateConfigDeliveryCustom', params);
